import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../context/AuthContext'
import { api } from '../lib/api'
import drinxLogo from '../assets/drinx_logo_white.png'

const schema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(1, 'Password required'),
})
type FormData = z.infer<typeof schema>

export default function Login() {
  const { login } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [expiredNotice, setExpiredNotice] = useState<string | null>(null)
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  async function onSubmit(data: FormData) {
    setError(null)
    setExpiredNotice(null)
    try {
      await login(data.email, data.password)
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { code?: string; email?: string } } }
      // PCI DSS 8.3.9 — the worker returns 403 password_expired when the
      // admin's password is > 90 days old. Kick off the forgot-password
      // flow automatically so the admin isn't left guessing why login
      // isn't working. We POST to /auth/forgot-password with their email
      // (the worker returns 200 regardless of whether the account exists,
      // to avoid leaking) then show a notice + park on the page.
      if (err.response?.data?.code === 'password_expired') {
        const email = err.response.data.email ?? data.email
        try {
          await api.post('/auth/forgot-password', { email })
        } catch { /* silent — same treatment as the forgot-password page */ }
        setExpiredNotice(
          `Your password is over 90 days old. We've emailed ${email} a reset link — check your inbox to set a new one.`
        )
        return
      }
      if (err.response?.data?.code === 'account_inactive') {
        setError('Your account is deactivated. Contact a super admin.')
        return
      }
      // Distinguish server errors from bad credentials — otherwise a
      // worker outage or a bad deploy shows the same message as a real
      // wrong-password, which sent me down a wild-goose chase during the
      // password-history rollout (missing migration column silently 500'd
      // and every admin saw "Incorrect email or password").
      const status = err.response?.status
      if (status && status >= 500) {
        setError('Sign-in service is having trouble. Try again in a moment; if it persists, contact support.')
        return
      }
      if (!status) {
        setError('Cannot reach the sign-in service. Check your connection and try again.')
        return
      }
      setError('Incorrect email or password')
    }
  }

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-1">
            <img src={drinxLogo} alt="DrinX" className="h-10 w-auto" />
          </div>
          <p className="text-gray-500 text-sm">Admin Portal</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1.5">Email</label>
            <input
              {...register('email')}
              type="email"
              autoComplete="email"
              className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] transition-colors"
              placeholder="admin@drinx.jo"
            />
            {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm text-gray-400">Password</label>
              <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-[#C41E3A] transition-colors">
                Forgot password?
              </Link>
            </div>
            <input
              {...register('password')}
              type="password"
              autoComplete="current-password"
              className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] transition-colors"
              placeholder="••••••••••••"
            />
            {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {expiredNotice && (
            <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
              {expiredNotice}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#C41E3A] hover:bg-[#a51830] disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-lg transition-colors"
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
